import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Button, Col, Container, Form, Row, Table, Tabs, Tab, Tooltip, OverlayTrigger } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { PlusIcon, MinusIcon, TimesIcon, TrashIcon } from "@simplex/sx-icons";

import { SXLoadComboPart } from "./SXInputLoadComboPart";
import { SXDropdown } from "@common/components/ui-components/SXDropdown";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { PopUpModal } from "./modals/common/PopUp";

import { AlertMessage } from "../helpers/AlertMessage";
import { getMtrl } from "@common/helpers/protoGen";
import { ng } from "@common/ngds";
import { getLoadcaseX } from "@common/helpers/protoGen";

import {
  LOAD_COMBINATION_TYPES_FOR_DROPDOWN,
  COA_TYPE_G1,
  COA_TYPE_G2,
  byLimitState,
  createEnvelopes,
  updateActiveLcosInContext,
} from "@common/helpers/funlib";
import { createDefaultLcombPart, createDefaultLoadcombination } from "@common/helpers/defaultObject";

import { useAppSelector, useAppDispatch } from "../app/hooks";
import { selectUser } from "../features/user/userSlice";
import { useProject } from "../features/projects/projectSlice";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { add } from "lodash-es";
import { openModal } from "../features/ui/uiSlice";
import { LCOGeneratorModalId } from "./modals/LCOGeneratorModal";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";

///////////////////////////////////////////////////////////////////////////////////////////////////

// Constants

const maxLoadcombinationNameLength = 24;

interface SXProjectInputLoadCombinationsProps {}

export const SXProjectInputLoadCombinations = (props: SXProjectInputLoadCombinationsProps) => {
  // --- Contexts ---
  const { t } = useTranslation();
  const user = useAppSelector(selectUser);
  const {
    project: selProject,
    addLoadCombination,
    deleteLoadCombination,
    updateLoadCombinationName,
    updateLoadCombinationType,
    addLoadCombinationPart,
    updateLoadCombinationCoa,
    deleteLoadCombinationPart,
    updateDominatingLCA,
    updateFavorableLCA,
    considerInAnalysis,
  } = useProject();
  const dispatch = useAppDispatch();

  // --- Data from Redux state ---
  const projDataJson = selProject?.data?.data?.input;

  const { loadcases, loadcombinations, beam, na, gen, structure, stage, loading } = useMemo(() => {
    const input = projDataJson ? ng.input.Data.fromObject(projDataJson) : null;
    const structure = input?.structures?.[0] ?? null;
    const stage = structure?.stages?.[0] ?? null;
    const loading = structure?.loading ?? null;
    const loadcases = loading?.loadcases ?? [];
    const loadcombinations = loading?.combinations ?? [];
    const loads = loading?.loads ?? [];
    const element = structure?.elements?.[0] ?? null;
    const beam = element?.getBeam() ?? null;

    let na = ng.eurocode.Annex.ANNEX_UNSPECIFIED;
    let gen = ng.eurocode.Generation.GENERATION_1;
    const code = input?.code;
    if (code?.type === "ec") {
      na = code.ec?.nationalAnnex ?? ng.eurocode.Annex.ANNEX_UNSPECIFIED;
      gen = code.ec?.generation || ng.eurocode.Generation.GENERATION_1;
    }

    return {
      loading,
      loadcases,
      loadcombinations,
      loads,
      beam,
      na,
      gen,
      structure,
      stage,
    };
  }, [projDataJson]);

  // --- Local UI state only ---
  const [conAnalysisUser, setConAnalysisUser] = useState(true);
  const [conAnalysisGen, setConAnalysisGen] = useState(true);

  const setTimber = (_: boolean) => {};
  // --- Effects ---
  useEffect(() => {
    // updateActiveLcosInContext(context, localisation, lang, props.pushChanges);
    // A group counts as used as soon as any of its combinations is active. Reading only the first
    // combination made the switch lie whenever the group held a mix, e.g. after adding a new
    // combination to a group that had been switched off.
    const groupIsUsed = (userDefined: boolean) =>
      loadcombinations.some((lco) => lco.userDefined === userDefined && !lco.deactive);

    setConAnalysisUser(groupIsUsed(true));
    setConAnalysisGen(groupIsUsed(false));
  }, [loadcombinations]);

  // --- project.data as ng.input.IData might be wrong choice here, we'll see... ---
  useEffect(() => {
    if (beam && beam.mtrlGuid && projDataJson) {
      const input = ng.input.Data.fromObject(projDataJson);
      const mtrl = getMtrl(beam.mtrlGuid, input) ?? new ng.material.Data();
      setTimber(mtrl.type === ng.material.Mtrl.MTRL_TIMBER);
    } else {
      setTimber(false);
    }
  }, [beam, projDataJson]);

  //---------------------------------------------------------------------------------

  // --- Load combo helper functions for add, update, and delete ---

  const addLoadCombinationFunc = () => {
    let newLC = createDefaultLoadcombination(loadcases);
    if (!newLC || !newLC.id || !newLC.id.guid) {
      console.error("Invalid newLC, aborting addLoadcombinationRedux.", newLC);
      return;
    }
    if (!stage) {
      return;
    }

    // Find a unique name for the new load combination on the format "LCO #"
    const userLcos = loadcombinations.filter((lc) => lc.userDefined);
    const existingNames = new Set(loadcombinations.map((lc) => lc.id?.name));
    let counter = 1;
    do {
      newLC.id.name = `LCO ${userLcos.length + counter}`;
      counter++;
    } while (existingNames.has(newLC.id.name));

    addLoadCombination(newLC);
  };

  const addLoadCombinationPartFunc = async (lcomb: ng.loadcombination.IData) => {
    let newPart = createDefaultLcombPart(loadcases, lcomb);
    if (newPart && lcomb.id?.guid) {
      await addLoadCombinationPart(lcomb.id.guid, newPart);
    }
  };

  // --- React response ---
  const lc_user = loadcombinations.filter((lcomb) => lcomb.userDefined === true).sort(byLimitState);
  const userDefinedTab = (
    <Tab eventKey="userDefined" title={t("user_defined_.label")}>
      <Container className="sx-table-style-for-custom-table" style={{ paddingBottom: 10 }}>
        <Row className="ps-1">
          <Col xs={4} className="p-0">
            <Form.Check
              type="switch"
              className="sx-switch"
              id="considerInAnalysisCheck_Usr"
              name={"favo"}
              label={t("use_.label")}
              disabled={lc_user.length === 0}
              checked={conAnalysisUser}
              onChange={(e) => {
                setConAnalysisUser(e.target.checked);
                considerInAnalysis(e.target.checked, "userDefined");
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col className="">
            {/** TABLE HEADER */}
            <Row
              className="table-header align-items-center"
              style={{
                marginBottom: 0,
                height: 38.5,
                borderRadius: "10px 10px 0px 0px",
                borderTop: "1px solid #e3dfdf",
                borderLeft: "1px solid #e3dfdf",
                borderRight: "1px solid #e3dfdf",
                borderBottom: "none",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Col xs={6} className="border-none">
                <Row>
                  <Col xs={1} style={{ border: 0, paddingLeft: 0 }}></Col>
                  <Col xs={6} className="table-header-bg" style={{ textAlign: "left" }}>
                    {t("loadcomb_name_.label")}
                  </Col>
                  <Col xs={5} className="table-header-bg ps-0" style={{ textAlign: "left" }}>
                    {t("loadcomb_type_.label")}
                  </Col>
                </Row>
              </Col>

              {/** Parts header */}
              <Col xs={6} id="parts-header">
                <Row>
                  <Col xs={10}>
                    <Row>
                      <Col xs={6} className="table-header-bg">
                        {t("loadcomb_loadcase_.label")}
                      </Col>

                      <Col xs={6} className=" table-header-bg">
                        {t("loadcomb_factor_.label")}
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={2} style={{ border: 0 }}></Col>
                </Row>
              </Col>
            </Row>
            <>
              {lc_user.length > 0 &&
                lc_user.map((lcomb: ng.loadcombination.IData, index: number) => {
                  // if (lcomb.userDefined === false) {
                  let loadcombTypeItem = LOAD_COMBINATION_TYPES_FOR_DROPDOWN.find((m) => m.id === lcomb.type);

                  let coaLst = [];

                  if (gen === ng.eurocode.Generation.GENERATION_1) {
                    let coaLstG1 = COA_TYPE_G1;

                    switch (loadcombTypeItem?.id) {
                      case ng.loadcombination.Type.TYPE_CHARACTERISTIC:
                        coaLstG1 = [COA_TYPE_G1[4]];
                        break;
                      case ng.loadcombination.Type.TYPE_FREQUENT:
                        coaLstG1 = [COA_TYPE_G1[5]];
                        break;
                      case ng.loadcombination.Type.TYPE_QUASI_PERMANENT:
                        coaLstG1 = [COA_TYPE_G1[6]];
                        break;
                      case ng.loadcombination.Type.TYPE_ACCIDENTAL:
                      case ng.loadcombination.Type.TYPE_FIRE:
                      case ng.loadcombination.Type.TYPE_SEISMIC:
                        coaLstG1 = [COA_TYPE_G1[3]];
                        break;
                      case ng.loadcombination.Type.TYPE_ULTIMATE:
                        if (na === ng.eurocode.Annex.ANNEX_DENMARK) {
                          coaLst = [coaLstG1[0], coaLstG1[1], coaLstG1[2]];
                        } else {
                          coaLst = [coaLstG1[0], coaLstG1[1], coaLstG1[2]];
                        }
                        break;
                      default:
                        break;
                    }

                    coaLst = coaLstG1;
                  } else {
                    let coaLstG2 = COA_TYPE_G2;

                    switch (loadcombTypeItem?.id) {
                      case ng.loadcombination.Type.TYPE_CHARACTERISTIC:
                        coaLstG2 = [COA_TYPE_G2[7]];
                        break;
                      case ng.loadcombination.Type.TYPE_FREQUENT:
                        coaLstG2 = [COA_TYPE_G2[8]];
                        break;
                      case ng.loadcombination.Type.TYPE_QUASI_PERMANENT:
                        coaLstG2 = [COA_TYPE_G2[9]];
                        break;
                      case ng.loadcombination.Type.TYPE_ACCIDENTAL:
                      case ng.loadcombination.Type.TYPE_FIRE:
                        coaLstG2 = [COA_TYPE_G2[5]];
                        break;
                      case ng.loadcombination.Type.TYPE_SEISMIC:
                        coaLstG2 = [COA_TYPE_G2[6]];
                        break;
                      case ng.loadcombination.Type.TYPE_ULTIMATE:
                        if (na === ng.eurocode.Annex.ANNEX_DENMARK) {
                          coaLst = [coaLstG2[0], coaLstG2[1], coaLstG2[2], coaLstG2[3], coaLstG2[4]];
                        } else {
                          coaLst = [coaLstG2[0], coaLstG2[1], coaLstG2[2], coaLstG2[3], coaLstG2[4]];
                        }
                        break;
                      default:
                        break;
                    }

                    coaLst = coaLstG2;
                  }

                  let coa = coaLst.find((m) => m.id === lcomb.coa || ng.loadcombination.CoaType.COA_TYPE_UNSPECIFIED);

                  let dominating = lcomb.dominatingLcaseGuid || null;

                  for (let lca of lcomb.parts) {
                    if (lca.lcaseGuid == null) continue;

                    let tmpLca = structure ? getLoadcaseX(lca.lcaseGuid, structure) : null;

                    if (tmpLca && tmpLca.type) {
                      if (
                        tmpLca.type === ng.loadcase.Type.TYPE_PERMANENT_LOAD ||
                        tmpLca.type === ng.loadcase.Type.TYPE_SELF_WEIGHT ||
                        tmpLca.type === ng.loadcase.Type.TYPE_SOIL_SELF_WEIGHT
                      ) {
                        if (
                          lcomb.type !== ng.loadcombination.Type.TYPE_CHARACTERISTIC &&
                          lcomb.type !== ng.loadcombination.Type.TYPE_FREQUENT &&
                          lcomb.type !== ng.loadcombination.Type.TYPE_QUASI_PERMANENT
                        ) {
                        }
                      } else {
                        if (dominating === null) {
                          //set the first non-permanent load as dominating if not set before
                          lcomb.dominatingLcaseGuid = lca.lcaseGuid;
                          dominating = lca.lcaseGuid;
                        }
                      }
                    }
                  }

                  return (
                    <Row
                      key={"combination" + index}
                      id={"table-body-load-combo" + index}
                      className={`${index === 0 ? "first-loadcombo-table-row" : "loadcombo-table-row"} justify-content-center align-items-start`}
                    >
                      <Col xs={6}>
                        <Row
                          style={{ height: 38.5, borderBottom: "1px solid #e3dfdf" }}
                          className="justify-content-center align-items-center"
                        >
                          <Col xs={1} className=" m-0">
                            <span
                              style={{ paddingLeft: 0, cursor: "pointer" }}
                              onClick={() => {
                                if (lcomb.id != null && lcomb.id.guid != null) {
                                  deleteLoadCombination(lcomb.id.guid);
                                }
                              }}
                            >
                              <MinusIcon size={12} />
                            </span>
                          </Col>
                          <Col xs={6} style={{ paddingTop: 8 }}>
                            <SXEditField
                              tableView
                              textAlign="left"
                              id={"lcombo_name_" + index}
                              type="text"
                              onUpdate={(value: string, revertValue: () => void) => {
                                if (value.length > maxLoadcombinationNameLength) {
                                  let msg = t("MSG_loadcombination_name_too_long_.label");
                                  let context = value + " > " + maxLoadcombinationNameLength.toString();
                                  AlertMessage("error_", t("error_label"), context, msg);
                                  revertValue();
                                  return;
                                }

                                let foundName = loadcombinations.find(
                                  (loadcombo) => loadcombo.id?.name === value && loadcombo.id?.guid !== lcomb.id?.guid,
                                );

                                if (!foundName) {
                                  if (lcomb.id != null && lcomb.id.guid != null) {
                                    updateLoadCombinationName(lcomb.id.guid, value);
                                  }
                                } else {
                                  let msg = t("MSG_loadcombination_name_already_exists_.label");
                                  AlertMessage("error_", t("error_label"), value, msg);
                                  revertValue();
                                }
                              }}
                              val={lcomb.id?.name ?? ""}
                            ></SXEditField>
                          </Col>
                          <Col
                            xs={5}
                            style={{
                              textAlign: "left",
                              padding: 0,
                            }}
                          >
                            <SXDropdown
                              tableView
                              name={"loadcombo_typ_dropdown_" + index}
                              onUpdate={async (value) => {
                                try {
                                  if (value === null) {
                                    return;
                                  }
                                  if (lcomb.id != null && lcomb.id.guid != null) {
                                    await updateLoadCombinationType(lcomb.id.guid, value.id);
                                  }
                                } catch (x) {
                                  report_exception(t, x);
                                }
                              }}
                              list={LOAD_COMBINATION_TYPES_FOR_DROPDOWN}
                              val={loadcombTypeItem}
                              lang=""
                            />
                          </Col>
                        </Row>
                      </Col>

                      {/** PARTS */}
                      <Col
                        xs={6}
                        id="parts-header-user"
                        style={{
                          marginLeft: "-1px",
                        }}
                      >
                        {lcomb !== null &&
                          lcomb.parts &&
                          lcomb.userDefined === true &&
                          lcomb.parts.map((part, index) => {
                            return (
                              <Row
                                key={"part" + index}
                                id="table-body-load-combo"
                                className="justify-content-center align-items-center"
                              >
                                <Col xs={10} className={"border-none"}>
                                  <SXLoadComboPart
                                    updateDominating={updateDominatingLCA}
                                    updateFavorable={updateFavorableLCA}
                                    keyIndex={index}
                                    isLast={index === lcomb.parts.length - 1}
                                    part={part}
                                    lcomb={lcomb}
                                    userDefined={true}
                                  />
                                </Col>

                                <Col xs={2} className="ps-0 text-center">
                                  <span
                                    onClick={async () => {
                                      try {
                                        if (lcomb.id != null && part.lcaseGuid != null) {
                                          await deleteLoadCombinationPart(lcomb.id.guid, part.lcaseGuid);
                                        }
                                      } catch (x) {
                                        report_exception(t, x);
                                      }
                                    }}
                                    className="delete-row-btn ps-0 px-0 "
                                  >
                                    {" "}
                                    <TimesIcon size={12} />
                                  </span>
                                </Col>
                              </Row>
                            );
                          })}
                      </Col>
                      {/** BOTTOM ROW - Add part */}
                      <Row className="ps-0">
                        <Col className="px-0">
                          <OverlayTrigger
                            placement="bottom"
                            delay={{ show: 1000, hide: 200 }}
                            overlay={<Tooltip id="tooltip">{t("add_part_.label")}</Tooltip>}
                          >
                            <span
                              className="addrow add-row-btn pointer mt-0"
                              id={"addPartButton"}
                              onClick={async () => {
                                try {
                                  if (lcomb != null) {
                                    await addLoadCombinationPartFunc(lcomb);
                                  }
                                } catch (x) {
                                  report_exception(t, x);
                                }
                              }}
                            >
                              {" "}
                              <PlusIcon size={12} />
                            </span>
                          </OverlayTrigger>
                        </Col>
                      </Row>
                    </Row>
                  );
                  //  }
                })}
            </>
          </Col>
        </Row>
        {/** BOTTOM ROW - load combo */}
        <Row className="mb-2" style={{ paddingTop: 10, paddingLeft: 0 }}>
          <Col className="border-none ps-0">
            <Button
              className="secondary-btn"
              size="sm"
              id={"addLoadComboButton"}
              onClick={() => {
                addLoadCombinationFunc();
              }}
            >
              {" "}
              <PlusIcon size={12} /> {t("add_loadcombination_.label")}
            </Button>
          </Col>
        </Row>
      </Container>
    </Tab>
  );

  const lc_generated = loadcombinations.filter((lcomb) => lcomb.userDefined === false).sort(byLimitState);
  const generatedTab = (
    <Tab eventKey="generated" title={t("generated_.label")}>
      <Container className="sx-table-style-for-custom-table" style={{ paddingBottom: 10 }}>
        <Row className="ps-1 mb-2 align-items-center">
          <Col xs={4} className="p-0">
            <Form.Check
              type="switch"
              className="sx-switch"
              id="considerInAnalysisCheck_Gen"
              name={"favo"}
              label={t("use_.label")}
              disabled={lc_generated.length === 0}
              checked={conAnalysisGen}
              onChange={(e) => {
                setConAnalysisGen(e.target.checked);
                considerInAnalysis(e.target.checked, "generated");
              }}
            />
          </Col>
          <Col className="p-0 d-flex justify-content-end">
            <Button
              className="secondary-btn"
              size="sm"
              id={"generateLoadComboButton"}
              onClick={() => {
                dispatch(openModal(LCOGeneratorModalId));
              }}
            >
              {t("generate_load_combinations_.label")}
            </Button>
          </Col>
        </Row>

        <Row>
          <Col>
            {/** TABLE HEADER */}
            <Row
              className="table-header align-items-center"
              style={{
                marginBottom: 0,
                height: 38.5,
                borderRadius: "10px 10px 0px 0px",
                borderTop: "1px solid #e3dfdf",
                borderLeft: "1px solid #e3dfdf",
                borderRight: "1px solid #e3dfdf",
                borderBottom: "none",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Col xs={6} className="border-none">
                <Row>
                  <Col xs={4} className="table-header-bg" style={{ textAlign: "left" }}>
                    {t("loadcomb_name_.label")}
                  </Col>
                  <Col xs={5} className="table-header-bg ps-0" style={{ textAlign: "left" }}>
                    {t("loadcomb_type_.label")}
                  </Col>
                  <Col xs={3} className="table-header-bg ps-0" style={{ textAlign: "center" }}>
                    {t("coa_.label")}
                  </Col>
                </Row>
              </Col>

              {/** Parts header */}
              <Col id="parts-header" xs={6}>
                <Row>
                  <Col className="no-border">
                    <Row>
                      <Col xs={11}>
                        <Row className="p-0">
                          <Col xs={4} className="table-header-bg">
                            {t("loadcomb_loadcase_.label")}
                          </Col>
                          <Col xs={3} className="table-header-bg text-center ps-0">
                            {t("main_.label")}
                          </Col>

                          <Col xs={3} className="table-header-bg text-center ps-0">
                            {t("favorable_.label")}
                          </Col>

                          <Col xs={2} className=" table-header-bg">
                            {t("loadcomb_factor_.label")}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={1} style={{ border: 0 }}></Col>
                </Row>
              </Col>
            </Row>
            <>
              {lc_generated.length > 0 &&
                lc_generated.map((lcomb: ng.loadcombination.IData, index: number) => {
                  let loadcombTypeItem = LOAD_COMBINATION_TYPES_FOR_DROPDOWN.find((m) => m.id === lcomb.type);

                  let coaLst = [];

                  if (gen === ng.eurocode.Generation.GENERATION_1) {
                    let coaLstG1 = COA_TYPE_G1;

                    switch (loadcombTypeItem?.id) {
                      case ng.loadcombination.Type.TYPE_CHARACTERISTIC:
                        coaLstG1 = [COA_TYPE_G1[4]];
                        break;
                      case ng.loadcombination.Type.TYPE_FREQUENT:
                        coaLstG1 = [COA_TYPE_G1[5]];
                        break;
                      case ng.loadcombination.Type.TYPE_QUASI_PERMANENT:
                        coaLstG1 = [COA_TYPE_G1[6]];
                        break;
                      case ng.loadcombination.Type.TYPE_ACCIDENTAL:
                      case ng.loadcombination.Type.TYPE_FIRE:
                      case ng.loadcombination.Type.TYPE_SEISMIC:
                        coaLstG1 = [COA_TYPE_G1[3]];
                        break;
                      case ng.loadcombination.Type.TYPE_ULTIMATE:
                        if (na === ng.eurocode.Annex.ANNEX_DENMARK) {
                          coaLst = [coaLstG1[0], coaLstG1[1], coaLstG1[2]];
                        } else {
                          coaLst = [coaLstG1[0], coaLstG1[1], coaLstG1[2]];
                        }
                        break;
                      default:
                        break;
                    }

                    coaLst = coaLstG1;
                  } else {
                    let coaLstG2 = COA_TYPE_G2;

                    switch (loadcombTypeItem?.id) {
                      case ng.loadcombination.Type.TYPE_CHARACTERISTIC:
                        coaLstG2 = [COA_TYPE_G2[7]];
                        break;
                      case ng.loadcombination.Type.TYPE_FREQUENT:
                        coaLstG2 = [COA_TYPE_G2[8]];
                        break;
                      case ng.loadcombination.Type.TYPE_QUASI_PERMANENT:
                        coaLstG2 = [COA_TYPE_G2[9]];
                        break;
                      case ng.loadcombination.Type.TYPE_ACCIDENTAL:
                      case ng.loadcombination.Type.TYPE_FIRE:
                        coaLstG2 = [COA_TYPE_G2[5]];
                        break;
                      case ng.loadcombination.Type.TYPE_SEISMIC:
                        coaLstG2 = [COA_TYPE_G2[6]];
                        break;
                      case ng.loadcombination.Type.TYPE_ULTIMATE:
                        if (na === ng.eurocode.Annex.ANNEX_DENMARK) {
                          coaLst = [coaLstG2[0], coaLstG2[1], coaLstG2[2], coaLstG2[3], coaLstG2[4]];
                        } else {
                          coaLst = [coaLstG2[0], coaLstG2[1], coaLstG2[2], coaLstG2[3], coaLstG2[4]];
                        }
                        break;
                      default:
                        break;
                    }

                    coaLst = coaLstG2;
                  }

                  let coa = coaLst.find((m) => m.id === lcomb.coa || ng.loadcombination.CoaType.COA_TYPE_UNSPECIFIED);

                  let dominating = lcomb.dominatingLcaseGuid || null;

                  for (let lca of lcomb.parts) {
                    if (lca.lcaseGuid == null) continue;

                    let tmpLca = structure ? getLoadcaseX(lca.lcaseGuid, structure) : null;

                    if (tmpLca && tmpLca.type) {
                      if (
                        tmpLca.type === ng.loadcase.Type.TYPE_PERMANENT_LOAD ||
                        tmpLca.type === ng.loadcase.Type.TYPE_SELF_WEIGHT ||
                        tmpLca.type === ng.loadcase.Type.TYPE_SOIL_SELF_WEIGHT
                      ) {
                        if (
                          lcomb.type !== ng.loadcombination.Type.TYPE_CHARACTERISTIC &&
                          lcomb.type !== ng.loadcombination.Type.TYPE_FREQUENT &&
                          lcomb.type !== ng.loadcombination.Type.TYPE_QUASI_PERMANENT
                        ) {
                        }
                      } else {
                        if (dominating === null) {
                          //set the first non-permanent load as dominating if not set before
                          lcomb.dominatingLcaseGuid = lca.lcaseGuid;
                          dominating = lca.lcaseGuid;
                        }
                      }
                    }
                  }

                  return (
                    <Row
                      key={"combination" + index}
                      id={"table-body-load-combo" + index}
                      className={`${index === 0 ? "first-loadcombo-table-row" : "loadcombo-table-row"} justify-content-center align-items-start`}
                    >
                      <Col xs={6} className="border-none">
                        <Row
                          style={{ height: 38.5, borderBottom: "1px solid #e3dfdf" }}
                          className="justify-content-center align-items-center"
                        >
                          <Col xs={4} style={{ paddingTop: 8 }}>
                            <SXEditField
                              disabled={true}
                              tableView
                              textAlign="left"
                              id={"lcombo_name_" + index}
                              type="text"
                              onUpdate={(value: string, revertValue: () => void) => {
                                if (value.length > maxLoadcombinationNameLength) {
                                  let msg = t("MSG_loadcombination_name_too_long_.label");
                                  let context = value + " > " + maxLoadcombinationNameLength.toString();
                                  AlertMessage("error_", t("error_label"), context, msg);
                                  revertValue();
                                  return;
                                }

                                let foundName = loadcombinations.find(
                                  (loadcombo) => loadcombo.id?.name === value && loadcombo.id?.guid !== lcomb.id?.guid,
                                );

                                if (!foundName) {
                                  if (lcomb.id != null && lcomb.id.guid != null) {
                                    updateLoadCombinationName(lcomb.id.guid, value);
                                  }
                                } else {
                                  let msg = t("MSG_loadcombination_name_already_exists_.label");
                                  AlertMessage("error_", t("error_label"), value, msg);
                                  revertValue();
                                }
                              }}
                              val={lcomb.id?.name ?? ""}
                            ></SXEditField>
                          </Col>
                          <Col
                            xs={5}
                            style={{
                              textAlign: "left",
                              padding: 0,
                            }}
                          >
                            <SXDropdown
                              tableView
                              disabled={true}
                              name={"loadcombo_typ_dropdown_" + index}
                              onUpdate={async (value) => {
                                try {
                                  if (value === null) {
                                    return;
                                  }
                                  if (lcomb.id != null && lcomb.id.guid != null) {
                                    await updateLoadCombinationType(lcomb.id.guid, value.id);
                                  }
                                } catch (x) {
                                  report_exception(t, x);
                                }
                              }}
                              list={LOAD_COMBINATION_TYPES_FOR_DROPDOWN}
                              val={loadcombTypeItem}
                              lang=""
                            />
                          </Col>
                          <Col
                            xs={3}
                            style={{
                              textAlign: "center",
                              padding: 0,
                            }}
                          >
                            <SXDropdown
                              disabled={true}
                              tableView
                              name={"coa_" + index}
                              id={"coadropdown_" + index}
                              dataTestId={"dropdown_coa"}
                              onUpdate={async (value) => {
                                try {
                                  if (value === null) {
                                    return;
                                  }
                                  if (lcomb.id != null && lcomb.id.guid != null) {
                                    await updateLoadCombinationCoa(lcomb.id.guid, value.id);
                                  }
                                } catch (x) {
                                  report_exception(t, x);
                                }
                              }}
                              list={coaLst}
                              val={coa}
                              lang=""
                            />
                          </Col>
                        </Row>
                        <Row>&nbsp;</Row> {/* Space between load combo header and parts header */}
                        <Row>&nbsp;</Row> {/* Space between load combo header and parts header */}
                      </Col>

                      {/** PARTS */}
                      <Col
                        id="parts-header-user"
                        xs={6}
                        style={{
                          marginLeft: "-1px",
                        }}
                      >
                        {lcomb !== null &&
                          lcomb.parts &&
                          lcomb.parts.map((part, index) => {
                            return (
                              <Row
                                key={"part" + index}
                                id="table-body-load-combo"
                                className="justify-content-center align-items-center"
                              >
                                <Col xs={11} className={"border-none"}>
                                  <SXLoadComboPart
                                    updateDominating={updateDominatingLCA}
                                    updateFavorable={updateFavorableLCA}
                                    keyIndex={index}
                                    isLast={index === lcomb.parts.length - 1}
                                    part={part}
                                    lcomb={lcomb}
                                    userDefined={false}
                                  />
                                </Col>
                              </Row>
                            );
                          })}
                      </Col>
                      {/** BOTTOM ROW - Add part */}
                      {/* <Row className="ps-0">
                                  <Col className="ps-0 m-0" xs={4}>
                                    <Button
                                      className="secondary-btn"
                                      onClick={() => {
                                        if (lcomb.id != null && lcomb.id.guid != null) {
                                          deleteLoadCombination(lcomb.id.guid);
                                        }
                                      }}
                                    >
                                      <TrashIcon size={12} />
                                    </Button>
                                  </Col>

                                  <Col className="px-0">
                                    <span
                                      className="addrow add-row-btn pointer "
                                      id={"addPartButton"}
                                      onClick={() => {
                                        if (lcomb != null) {
                                          addLoadCombinationPartFunc(lcomb);
                                        }
                                      }}
                                    >
                                      {" "}
                                      <PlusIcon size={12} />
                                    </span>
                                  </Col>
                                </Row> */}
                    </Row>
                  );
                })}
            </>
          </Col>
        </Row>
      </Container>
    </Tab>
  );

  return (
    <>
      {/* -------------------- LOAD CASE COMBOS --------------------- */}
      <Container className="ps-0 px-0">
        <Row>
          <Tabs
            id="load-combinations-tabs"
            defaultActiveKey="userDefined"
            className="simplex-custom-tabs"
            style={{ paddingLeft: 12 }}
          >
            {userDefinedTab}
            {generatedTab}
          </Tabs>
        </Row>
      </Container>
    </>
  );
};
